import { useState } from "react";
import { Box, Grid, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import {
  colorBorder,
  colorSurface,
  colorSurfaceHover,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
  radiusInput,
  shadowCard,
} from "src/design/tokens";
import { CustomButton } from "src/design/CustomButton";
import BookingSummaryCard from "./BookingSummaryCard";

export interface BookingDetailsValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string | null;
}

interface Props {
  selectedDate: Dayjs;
  selectedTime: string;
  guestCount: number;
  restaurantPhone: string;
  isPending: boolean;
  onSubmit: (values: BookingDetailsValues) => void;
  onBack: () => void;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: radiusInput,
    backgroundColor: colorSurfaceHover,
    fontSize: 14,
  },
};

const BookingDetailsForm = ({
  selectedDate,
  selectedTime,
  guestCount,
  restaurantPhone,
  isPending,
  onSubmit,
  onBack,
}: Props) => {
  const { t } = useTranslation();

  const [values, setValues] = useState<BookingDetailsValues>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    notes: null,
  });
  const [errors, setErrors] = useState<Partial<BookingDetailsValues>>({});

  const set =
    (field: keyof BookingDetailsValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((v) => ({ ...v, [field]: e.target.value }));
      setErrors((er) => ({ ...er, [field]: undefined }));
    };

  const validate = (): boolean => {
    const errs: Partial<BookingDetailsValues> = {};
    if (!values.firstName.trim()) errs.firstName = t("clientBooking.required");
    if (!values.lastName.trim()) errs.lastName = t("clientBooking.required");
    if (
      !values.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
    )
      errs.email = t("clientBooking.invalidEmail");
    if (!values.phone.trim()) errs.phone = t("clientBooking.required");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit(values);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1.5,
          cursor: "pointer",
          width: "fit-content",
          color: colorTextMuted,
          "&:hover": { color: colorTextPrimary },
        }}
        onClick={onBack}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={500}>
          {t("clientBooking.back")}
        </Typography>
      </Box>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {t("clientBooking.yourDetails")}
      </Typography>
      <Typography variant="body2" color={colorTextMuted} sx={{ mb: 2.5 }}>
        {t("clientBooking.completeReservation")}
      </Typography>

      <BookingSummaryCard
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        guestCount={guestCount}
      />

      <Box
        sx={{
          backgroundColor: colorSurface,
          border: `1px solid ${colorBorder}`,
          borderRadius: radiusCard,
          boxShadow: shadowCard,
          p: { xs: 2, sm: 2.5 },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              {t("clientBooking.firstName")} *
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="John"
              value={values.firstName}
              onChange={set("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              {t("clientBooking.lastName")} *
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Doe"
              value={values.lastName}
              onChange={set("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              {t("clientBooking.email")} *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="email"
              placeholder="john.doe@example.com"
              value={values.email}
              onChange={set("email")}
              error={!!errors.email}
              helperText={errors.email}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              {t("clientBooking.phone")} *
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={restaurantPhone}
              value={values.phone}
              onChange={set("phone")}
              error={!!errors.phone}
              helperText={errors.phone}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              {t("clientBooking.specialRequests")}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={t("clientBooking.specialRequestsPlaceholder")}
              value={values.notes ?? ""}
              onChange={set("notes")}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomButton
              color="black"
              fullWidth
              sx={{ borderRadius: radiusInput, py: 1.25 }}
              onClick={handleSubmit}
              disabled={isPending}
            >
              {t("clientBooking.confirmReservation")}
            </CustomButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BookingDetailsForm;
